import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import type { StatusCode } from "~/types/api";
import {
  createListRequest,
  updateListNameRequest,
  updateListStatusesRequest,
  deleteListRequest,
} from "../api/lists.api";

export const useCreateList = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      name: string;
      workflowId: number;
      statusCodes?: StatusCode[];
    }) => {
      const { name, workflowId, statusCodes } = params;
      const response = await createListRequest(workflowId, name);

      if (response.error) {
        throw response.error;
      }

      if (statusCodes && statusCodes.length > 0 && response.data) {
        const listId = (response.data as { id: number }).id;
        const updateStatusResponse = await updateListStatusesRequest(
          workflowId,
          listId,
          statusCodes
        );

        if (updateStatusResponse.error) {
          throw updateStatusResponse.error;
        }
      }

      return { response, workflowId };
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: configurationApi.queryOptions("get", "/api/workflow/{workflowId}/List", {
          params: { path: { workflowId: data.workflowId } },
        }).queryKey,
      });
      showToast("success", "List created successfully");
    },
    onError: (error) => {
      console.error("Failed to create list:", error);
      const errorMessage = getErrorMessage(error);
      showToast("error", errorMessage, "Failed to create list");
    },
  });
};

export const useUpdateList = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      listId: number;
      workflowId: number;
      name: string;
      originalName?: string;
      statusCodes?: StatusCode[];
      originalStatusCodes?: StatusCode[];
    }) => {
      const { listId, workflowId, name, originalName, statusCodes, originalStatusCodes } = params;
      const promises = [];

      if (originalName !== undefined && name !== originalName) {
        promises.push(updateListNameRequest(workflowId, listId, name));
      }

      if (statusCodes && originalStatusCodes) {
        const statusesChanged =
          statusCodes.length !== originalStatusCodes.length ||
          !statusCodes.every((sc) =>
            originalStatusCodes.some((o) => o.code === sc.code && o.sortOrder === sc.sortOrder)
          );

        if (statusesChanged) {
          promises.push(updateListStatusesRequest(workflowId, listId, statusCodes));
        }
      }

      if (promises.length > 0) {
        const results = await Promise.all(promises);
        for (const result of results) {
          if (result.error) {
            throw result.error;
          }
        }
      }

      return { workflowId, listId };
    },
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: configurationApi.queryOptions("get", "/api/workflow/{workflowId}/List", {
            params: { path: { workflowId: data.workflowId } },
          }).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: configurationApi.queryOptions(
            "get",
            "/api/workflow/{workflowId}/list/{listId}/status",
            { params: { path: { workflowId: data.workflowId, listId: data.listId } } }
          ).queryKey,
        }),
      ]);
      showToast("success", "List updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update list:", error);
      const errorMessage = getErrorMessage(error);
      showToast("error", errorMessage, "Failed to update list");
    },
  });
};

export const useDeleteList = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (params: { listId: number; workflowId: number }) => {
      const { listId, workflowId } = params;
      const { error } = await deleteListRequest(workflowId, listId);

      if (error) {
        throw error;
      }

      return { workflowId };
    },
    onSuccess: async (data) => {
      const queryKey = configurationApi.queryOptions("get", "/api/workflow/{workflowId}/List", {
        params: { path: { workflowId: data.workflowId } },
      }).queryKey;
      await queryClient.invalidateQueries({ queryKey });
      showToast("success", "List deleted successfully");
    },
    onError: (error) => {
      console.error("Failed to delete list:", error);
      const errorMessage = getErrorMessage(error);
      showToast("error", errorMessage, "Failed to delete list");
    },
  });
};
